import moment from "moment";
import _ from "lodash";
import React, { useContext, useRef, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import StatsHelper from "../../classes/statsHelper";
import WaiversPlayersHelper from "../../classes/waiversPlayersHelper";
import BreakpointContext from "../../contexts/breakpointContext";
import useNflTeams from "../../hooks/queryResults/nfl/useNflTeams";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import usePlayerSeasonStats from "../../hooks/queryResults/usePlayerSeasonStats";
import useRosters from "../../hooks/queryResults/useRosters";
import useTeams from "../../hooks/queryResults/useTeams";
import useUserTeam from "../../hooks/queryResults/useUserTeam";
import useWaiversPlayers from "../../hooks/queryResults/useWaiversPlayers";
import useWeek from "../../hooks/queryResults/useWeek";
import useAdminMode from "../../hooks/useAdminMode";
import NflUtil from "../../util/nflUtil";
import TeamLogo from "../team/TeamLogo";
import Loading from "../ui/Loading";
import Pagination from "../ui/Pagination";
import { getPlayerName } from "./PlayerName";
import queryString from "query-string";
import styled from "styled-components";

const StyledSpan = styled.span`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export default function PlayerSeasonStatsTable({
  onClickAdd,
  onClickDrop,
  onClickTrade,
}) {
  const breakpoints = useContext(BreakpointContext);
  const history = useHistory();
  const location = useLocation();

  console.log("location", location);

  let { league: leagueUrl, season: seasonUrl } = useParams();
  const { playersData, playersQuery } = useAllPlayers();
  const { playerSeasonStatsData, playerSeasonStatsQuery } =
    usePlayerSeasonStats();
  const { teams, teamsQuery } = useTeams();
  const { rostersData, rostersQuery } = useRosters();
  const { waiversPlayersData, waiversPlayersQuery } = useWaiversPlayers();
  const userTeam = useUserTeam();
  const adminMode = useAdminMode();
  const searchRef = useRef();
  const { nflTeamsData, nflTeamsQuery } = useNflTeams();
  const { currentWeek } = useWeek();

  const [isSearchFilterOn, setIsSearchFilterOn] = useState(false);
  const [searchString, setSearchString] = useState("");

  const isLoading = useIsLoading(
    nflTeamsData,
    nflTeamsQuery,
    playersData,
    playersQuery,
    playerSeasonStatsData,
    playerSeasonStatsQuery,
    teams,
    teamsQuery,
    rostersData,
    rostersQuery,
    waiversPlayersData,
    waiversPlayersQuery,
    userTeam
  );
  if (isLoading) return <Loading />;

  const allPlayerIds = Object.keys(playersData.players);
  const playerIdsWithStats = playerSeasonStatsData.seasonStats.map(
    (ss) => ss.id
  );
  const playerIdsWithNoStats = _.difference(allPlayerIds, playerIdsWithStats);
  console.log("playerIdsWithNoStats", playerIdsWithNoStats);
  const seasonNoStats = playerIdsWithNoStats.map((playerId) => ({
    id: playerId,
  }));

  const isTaken = (playerId) => {
    return rostersData.rosters.players.hasOwnProperty(playerId);
  };

  const parsed = queryString.parse(location.search);

  let pageNum = parsed.pg || "1";
  pageNum = parseInt(pageNum, 10);
  const pageSize = 25;
  const start = (pageNum - 1) * pageSize;
  const end = start + pageSize;

  let { seasonStats } = playerSeasonStatsData;

  seasonStats = [...seasonStats, ...seasonNoStats];

  if (isSearchFilterOn) {
    let pattern = new RegExp("abc");
    if (isSearchFilterOn) {
      pattern = new RegExp(searchString, "i");
    }

    seasonStats = seasonStats.filter((ss) => {
      const playerId = ss.id;
      const player = playersData.players[playerId];
      const { firstName, lastName } = player;
      let checkString = `${firstName}${lastName}`.replace(/\s*/g, "").trim();
      if (player.nflTeamAbbr) checkString += player.nflTeamAbbr;
      return pattern.test(checkString);
    });
  } else {
    // filter out certain positions
    if (parsed.pos && parsed.pos !== "all") {
      seasonStats = seasonStats.filter((ss) => {
        const playerId = ss.id;
        const player = playersData.players[playerId];
        if (parsed.pos === "offense")
          return ["QB", "RB", "WR", "TE"].includes(player.pos);
        return parsed.pos.toUpperCase() === player.pos;
      });
    }

    // filter out certain player types
    if (parsed.players && parsed.players !== "all") {
      seasonStats = seasonStats.filter((ss) => {
        const playerId = ss.id;
        const isAvailable = !isTaken(playerId);
        if (parsed.players === "available") return isAvailable;
        else if (parsed.players === "taken") return !isAvailable;
        return true;
      });
    }
  }

  let statsOptions = ["Default"];
  if (["offense", "qb", "rb", "wr", "te"].includes(parsed.pos)) {
    statsOptions = [...statsOptions, "Pass", "Rush", "Rec", "Returns"];
  }

  const numPlayers = seasonStats.length;

  const waiversPlayersHelper = new WaiversPlayersHelper(waiversPlayersData);

  const handleAction = (actionType, playerIn, playerOut) => {
    const playersIn = [];
    const playersOut = [];
    if (playerIn) playersIn.push(playerIn);
    if (playerOut) playersOut.push(playerOut);

    history.push({
      pathname: `/${leagueUrl}/${seasonUrl}/${userTeam.idOrSlug}/roster`,
      state: { actionType, playersIn, playersOut },
    });
  };

  const handlePlayerSearch = () => {
    const searchIsOn = searchRef.current.value.length >= 3;
    setIsSearchFilterOn(searchIsOn);
    setSearchString(
      searchRef.current.value.replace(/\s*/g, "").toLowerCase().trim()
    );
    // parsed.pos = "all";
    // parsed.players = "all";
  };

  const statsType = parsed.stats || "default";

  const getHeaderColumns = () => {
    const pos = !!parsed.pos ? parsed.pos : "all";

    const defaultColumns = (
      <>
        <th>Action</th>
        <th title={"Rank"}>Rk</th>
        <th title={"Position Rank"}>PRk</th>
        <th>Pos</th>
        <th>Name</th>
        <th>NFL</th>
        <th>Team</th>
        <th>Bye</th>
        <th title={"Games Played"}>GP</th>
        <th title={"Total Points Scored"}>Pts</th>
        <th title={"Average per team game"}>Atg</th>
        <th title={"Average per game played"}>Avg</th>
      </>
    );
    const numDefaultColumns = 12;

    if (pos === "k") {
      return (
        <>
          <tr>
            <th colSpan={numDefaultColumns}>&nbsp;</th>
            <th colSpan={4}>Field Goals</th>
            <th colSpan={3}>Extra Points</th>
            <th>&nbsp;</th>
            <th colSpan={3}>Avg FG</th>
          </tr>
          <tr>
            {defaultColumns}
            <th>Made</th>
            <th>Att</th>
            <th>Pct</th>
            <th>Long</th>
            <th>Made</th>
            <th>Att</th>
            <th>Pct</th>
            <th title={"Blocked"}>Blk</th>
            <th>Att</th>
            <th>Made</th>
            <th>Miss</th>
          </tr>
        </>
      );
    } else if (pos === "def") {
      return (
        <>
          <tr>
            {defaultColumns}
            <th title={"Points Allowed"}>PA</th>
            <th title={"Sacks"}>Sac</th>
            <th title={"Takeaways"}>Tkwy</th>
            <th title={"Interceptions"}>Int</th>
            <th title={"Fumbles Recovered"}>Fum</th>
            <th title={"Touchdowns"}>TD</th>
            <th title={"Blocked Kicks"}>Blk</th>
            <th title={"Safeties"}>Saf</th>
            <th title={"Two Point Returns"}>2pt</th>
          </tr>
        </>
      );
    } else if (
      pos === "all" ||
      (pos === "offense" && statsType === "default")
    ) {
      return (
        <>
          <tr>
            <th colSpan={numDefaultColumns}>&nbsp;</th>
            <th colSpan={3}>Passing</th>
            <th colSpan={2}>Rushing</th>
            <th colSpan={2}>Receiving</th>
            <th colSpan={2}>Fumbles</th>
            <th>&nbsp;</th>
            <th>Ret</th>
          </tr>
          <tr>
            {defaultColumns}
            <th>Yds</th>
            <th>TD</th>
            <th>Int</th>
            <th>Yds</th>
            <th>TD</th>
            <th>Yds</th>
            <th>TD</th>
            <th>Num</th>
            <th>Lost</th>
            <th>2pt</th>
            <th>TD</th>
          </tr>
        </>
      );
    } else {
      if (statsType === "default") {
        if (pos === "qb") {
          return (
            <>
              <tr>
                <th colSpan={numDefaultColumns}>&nbsp;</th>
                <th colSpan={4}>Passing</th>
                <th colSpan={3}>Rushing</th>
                <th colSpan={2}>Fumbles</th>
              </tr>
              <tr>
                {defaultColumns}
                <th>Yds</th>
                <th>TD</th>
                <th>Int</th>
                <th>Rat</th>
                <th>Att</th>
                <th>Yds</th>
                <th>TD</th>
                <th>Num</th>
                <th>Lost</th>
                <th>2pt</th>
              </tr>
            </>
          );
        } else if (pos === "rb" || pos === "wr" || pos === "te") {
          return (
            <>
              <tr>
                <th colSpan={numDefaultColumns}>&nbsp;</th>
                <th colSpan={3}>Rushing</th>
                <th colSpan={4}>Receiving</th>
                <th colSpan={2}>Fumbles</th>
              </tr>
              <tr>
                {defaultColumns}
                <th>Att</th>
                <th>Yds</th>
                <th>TD</th>
                <th>Target</th>
                <th>Rec</th>
                <th>Yds</th>
                <th>TD</th>
                <th>Num</th>
                <th>Lost</th>
                <th>2pt</th>
              </tr>
            </>
          );
        }
      } else if (statsType === "pass") {
        return (
          <>
            <tr>
              <th colSpan={numDefaultColumns}>&nbsp;</th>
              <th colSpan={9}>Passing</th>
              <th colSpan={1}>2 Point</th>
            </tr>
            <tr>
              {defaultColumns}
              <th>Comp</th>
              <th>Att</th>
              <th>Pct</th>
              <th>Yds</th>
              <th>Avg</th>
              <th>TD</th>
              <th>Int</th>
              <th>Sac</th>
              <th>Rat</th>
              <th>Pass</th>
            </tr>
          </>
        );
      } else if (statsType === "rush") {
        return (
          <>
            <tr>
              <th colSpan={numDefaultColumns}>&nbsp;</th>
              <th colSpan={5}>Rushing</th>
              <th colSpan={2}>Fum</th>
              <th colSpan={1}>2 Point</th>
            </tr>
            <tr>
              {defaultColumns}
              <th>Att</th>
              <th>Yds</th>
              <th>Avg</th>
              <th>Long</th>
              <th>TD</th>
              <th>Num</th>
              <th>Lost</th>
              <th>Rush</th>
            </tr>
          </>
        );
      } else if (statsType === "rec") {
        return (
          <>
            <tr>
              <th colSpan={numDefaultColumns}>&nbsp;</th>
              <th colSpan={6}>Receiving</th>
              <th colSpan={2}>Fum</th>
              <th colSpan={1}>2 Point</th>
            </tr>
            <tr>
              {defaultColumns}
              <th>Targets</th>
              <th>Rec</th>
              <th>Yds</th>
              <th>Avg</th>
              <th>Long</th>
              <th>TD</th>
              <th>Num</th>
              <th>Lost</th>
              <th>Rec</th>
            </tr>
          </>
        );
      } else if (statsType === "returns") {
        return (
          <>
            <tr>
              <th colSpan={numDefaultColumns}>&nbsp;</th>
              <th colSpan={5}>Punt</th>
              <th colSpan={5}>Kick</th>
            </tr>
            <tr>
              {defaultColumns}
              <th>Ret</th>
              <th>Yds</th>
              <th>Avg</th>
              <th>Long</th>
              <th>TD</th>
              <th>Ret</th>
              <th>Yds</th>
              <th>Avg</th>
              <th>Long</th>
              <th>TD</th>
            </tr>
          </>
        );
      }
    }
  };

  const getStats = (ss) => {
    const pos = !!parsed.pos ? parsed.pos : "all";

    let { id: playerId, stats /*, scores */ } = ss;
    if (!stats) stats = {};
    const player = playersData.players[playerId];

    const pass = !!stats.pass
      ? stats.pass
      : {
          comp: 0,
          att: 0,
          pct: 0,
          yds: 0,
          avg: 0,
          td: 0,
          inter: 0,
          rating: 0,
          sacked: 0,
        };
    const rush = !!stats.rush
      ? stats.rush
      : { att: 0, yds: 0, avg: 0, long: 0, td: 0 };
    const fum = !!stats.fum ? stats.fum : { fum: 0, lost: 0 };
    const twoPoint = !!stats.twoPoint
      ? stats.twoPoint
      : { passSuccess: 0, rushSuccess: 0, recSuccess: 0 };

    const twoPointTotal =
      twoPoint.passSuccess + twoPoint.rushSuccess + twoPoint.recSuccess;

    const rec = !!stats.rec
      ? stats.rec
      : { target: 0, rec: 0, yds: 0, td: 0, avg: 0, long: 0 };

    const puntRet = !!stats.puntRet
      ? stats.puntRet
      : { ret: 0, yds: 0, avg: 0, long: 0, td: 0 };
    const kickRet = !!stats.kickRet
      ? stats.kickRet
      : { ret: 0, yds: 0, avg: 0, long: 0, td: 0 };

    const retTd = puntRet.td + kickRet.td;

    const kick = !!stats.kick ? stats.kick : {};
    const def = !!stats.def ? stats.def : {};

    if (pos === "k") {
      return (
        <>
          <td>{kick.fgNumMade}</td>
          <td>{kick.fgNumAtt}</td>
          <td>{(kick.fgPct * 100).toFixed(1)}</td>
          <td>{kick.fgLong}</td>
          <td>{kick.xptNumMade}</td>
          <td>{kick.xptNumAtt}</td>
          <td>{(kick.xptPct * 100).toFixed(1)}</td>
          <td>{kick.blocked}</td>
          <td>
            {(kick.fgNumMade
              ? _.mean(kick.fgYdsMade.concat(kick.fgYdsMissed))
              : 0
            ).toFixed(1)}
          </td>
          <td>{(kick.fgNumMade ? _.mean(kick.fgYdsMade) : 0).toFixed(1)}</td>
          <td>
            {(kick.fgNumAtt - kick.fgNumMade
              ? _.mean(kick.fgYdsMissed)
              : 0
            ).toFixed(1)}
          </td>
        </>
      );
    } else if (pos === "def") {
      console.log(def);
      return (
        <>
          <td title={"Points Allowed"}>{def.ptsAllow}</td>
          <td title={"Sacks"}>{def.sacks}</td>
          <td title={"Takeaways"}>{def.inter + def.fumRec}</td>
          <td title={"Interceptions"}>{def.inter}</td>
          <td title={"Fumbles Recovered"}>{def.fumRec}</td>
          <td title={"Touchdowns"}>{def.td}</td>
          <td title={"Blocked Kicks"}>{def.blocks}</td>
          <td title={"Safeties"}>{def.safety}</td>
          <td title={"Two Point Returns"}>{def.twoPoint}</td>
        </>
      );
    } else if (
      pos === "all" ||
      (pos === "offense" && statsType === "default")
    ) {
      if (player.pos === "K" || player.pos === "DEF") {
        const summary = StatsHelper.getSummary(player.pos, stats, true);
        return (
          <td colSpan={11} className={"left"}>
            {summary.map((line) => (
              <div>{line}</div>
            ))}
          </td>
        );
      } else {
        return (
          <>
            <td>{pass.yds}</td>
            <td>{pass.td}</td>
            <td>{pass.inter}</td>
            <td>{rush.yds}</td>
            <td>{rush.td}</td>
            <td>{rec.yds}</td>
            <td>{rec.td}</td>
            <td>{fum.fum}</td>
            <td>{fum.lost}</td>
            <td>{twoPointTotal}</td>
            <td>{retTd}</td>
          </>
        );
      }
    } else {
      if (statsType === "default") {
        if (pos === "offense") {
          return (
            <>
              <td>{pass.yds}</td>
              <td>{pass.td}</td>
              <td>{pass.inter}</td>
              <td>{rush.yds}</td>
              <td>{rush.td}</td>
              <td>{rec.yds}</td>
              <td>{rec.td}</td>
              <td>{fum.fum}</td>
              <td>{fum.lost}</td>
              <td>{twoPointTotal}</td>
              <td>{retTd}</td>
            </>
          );
        } else if (pos === "qb") {
          return (
            <>
              <td>{pass.yds}</td>
              <td>{pass.td}</td>
              <td>{pass.inter}</td>
              <td>{pass.rating.toFixed(1)}</td>
              <td>{rush.att}</td>
              <td>{rush.yds}</td>
              <td>{rush.td}</td>
              <td>{fum.fum}</td>
              <td>{fum.lost}</td>
              <td>{twoPointTotal}</td>
            </>
          );
        } else if (pos === "rb" || pos === "wr" || pos === "te") {
          return (
            <>
              <td>{rush.att}</td>
              <td>{rush.yds}</td>
              <td>{rush.td}</td>
              <td>{rec.target}</td>
              <td>{rec.rec}</td>
              <td>{rec.yds}</td>
              <td>{rec.td}</td>
              <td>{fum.fum}</td>
              <td>{fum.lost}</td>
              <td>{twoPointTotal}</td>
            </>
          );
        }
      } else if (statsType === "pass") {
        return (
          <>
            <td>{pass.comp}</td>
            <td>{pass.att}</td>
            <td>{(pass.pct * 100).toFixed(1)}</td>
            <td>{pass.yds}</td>
            <td>{pass.avg.toFixed(1)}</td>
            <td>{pass.td}</td>
            <td>{pass.inter}</td>
            <td>{pass.sacked}</td>
            <td>{pass.rating.toFixed(1)}</td>
            <td>{twoPoint.passSuccess}</td>
          </>
        );
      } else if (statsType === "rush") {
        return (
          <>
            <td>{rush.att}</td>
            <td>{rush.yds}</td>
            <td>{rush.avg.toFixed(1)}</td>
            <td>{rush.long}</td>
            <td>{rush.td}</td>
            <td>{fum.fum}</td>
            <td>{fum.lost}</td>
            <td>{twoPoint.passSuccess}</td>
          </>
        );
      } else if (statsType === "rec") {
        return (
          <>
            <td>{rec.target}</td>
            <td>{rec.rec}</td>
            <td>{rec.yds}</td>
            <td>{rec.avg.toFixed(1)}</td>
            <td>{rec.long}</td>
            <td>{rec.td}</td>
            <td>{fum.fum}</td>
            <td>{fum.lost}</td>
            <td>{twoPoint.passSuccess}</td>
          </>
        );
      } else if (statsType === "returns") {
        return (
          <>
            <td>{puntRet.ret}</td>
            <td>{puntRet.yds}</td>
            <td>{puntRet.avg.toFixed(1)}</td>
            <td>{puntRet.long}</td>
            <td>{puntRet.td}</td>
            <td>{kickRet.ret}</td>
            <td>{kickRet.yds}</td>
            <td>{kickRet.avg.toFixed(1)}</td>
            <td>{kickRet.long}</td>
            <td>{kickRet.td}</td>
          </>
        );
      }
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexFlow: "column nowrap",
      }}
    >
      <div
        style={{
          margin: "1rem 0",
          display: "flex",
          flexFlow: "row wrap",
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: "bold" }}>Filters</div>
        <div style={{ marginLeft: "2rem" }}>
          Pos:{" "}
          <select
            onChange={(event) => {
              parsed.pos = event.target.value;
              parsed.pg = 1;
              if (["all", "k", "def"].includes(parsed.pos))
                parsed.stats = "default";
              history.push(
                `${location.pathname}?${queryString.stringify(parsed)}`
              );
            }}
          >
            {["ALL", "Offense", "QB", "RB", "WR", "TE", "K", "DEF"].map(
              (pos, i) => {
                let isSelected;
                if (parsed.pos) {
                  if (parsed.pos.toLowerCase() === pos.toLowerCase())
                    isSelected = true;
                } else isSelected = pos === "ALL";
                return (
                  <option
                    key={i}
                    value={pos.toLowerCase()}
                    selected={isSelected}
                  >
                    {pos}
                  </option>
                );
              }
            )}
          </select>
        </div>
        <div style={{ marginLeft: "2rem" }}>
          Players:{" "}
          <select
            onChange={(event) => {
              parsed.players = event.target.value;
              parsed.pg = 1;
              history.push(
                `${location.pathname}?${queryString.stringify(parsed)}`
              );
            }}
          >
            {["ALL", "Available", "Taken"].map((playerType, i) => {
              let isSelected;
              if (parsed.players && parsed.players === playerType.toLowerCase())
                isSelected = true;
              else isSelected = playerType === "ALL";
              return (
                <option
                  key={i}
                  value={playerType.toLowerCase()}
                  selected={isSelected}
                >
                  {playerType}
                </option>
              );
            })}
          </select>
        </div>
        <div style={{ marginLeft: "2rem" }}>
          Stats:{" "}
          {statsOptions.length > 1 ? (
            <select
              onChange={(event) => {
                parsed.stats = event.target.value;
                history.push(
                  `${location.pathname}?${queryString.stringify(parsed)}`
                );
              }}
            >
              {statsOptions.map((statsType, i) => {
                let isSelected;
                if (parsed.stats && parsed.stats === statsType.toLowerCase())
                  isSelected = true;
                else isSelected = statsType === "Default";
                return (
                  <option
                    key={i}
                    value={statsType.toLowerCase()}
                    selected={isSelected}
                  >
                    {statsType}
                  </option>
                );
              })}
            </select>
          ) : (
            statsOptions[0]
          )}
        </div>
        <div style={{ marginLeft: "2rem" }}>
          <input
            ref={searchRef}
            type={"text"}
            placeholder={"Search players..."}
            onChange={handlePlayerSearch}
            style={{ width: breakpoints.sm ? "16rem" : "12rem" }}
          />
        </div>
      </div>
      <table
        style={{
          fontSize: "1rem",
          // fontFamily: "Verdana",
          fontFamily: "'BenchNine', sans-serif",
        }}
      >
        <thead>{getHeaderColumns()}</thead>
        <tbody>
          {seasonStats.slice(start, end).map((ss, i) => {
            const { id: playerId, stats, scores } = ss;

            const rank = scores?.atgRank;
            const posRank = scores?.atgPosRank;
            const player = playersData.players[playerId];

            let nflTeamAbbr = null;
            let byeWeek = null;
            if (player.nflTeamAbbr) {
              nflTeamAbbr = NflUtil.formatNflTeamAbbr(player.nflTeamAbbr);
              const nflTeam = nflTeamsData.nflTeams[player.nflTeamAbbr];
              if (nflTeam) byeWeek = nflTeam.byeWeek;
              if (byeWeek === currentWeek) {
                byeWeek = <span style={{ color: "red" }}>{byeWeek}</span>;
              }
            }

            let teamId = null;
            let team = null;
            if (rostersData.rosters.players.hasOwnProperty(player.id)) {
              teamId = rostersData.rosters.players[player.id];
              team = teams.getTeamById(teamId);
            }
            const isTaken = !!teamId;

            let teamCell = null;
            if (isTaken) {
              teamCell = <TeamLogo team={team} size={"sm"} />;
            } else {
              if (waiversPlayersHelper.isOnWaivers(player.id)) {
                const clearDueDate = waiversPlayersHelper.getClearDueDate(
                  player.id
                );
                const clearDueDateFormatted =
                  moment(clearDueDate).format("ddd M/D h:mm a");
                teamCell = (
                  <span title={"On waivers until " + clearDueDateFormatted}>
                    W
                  </span>
                );
              }
            }

            const handleAdd = onClickAdd
              ? () => onClickAdd(player.id)
              : () => handleAction("add", player.id);

            const handleDrop = onClickDrop
              ? () => onClickDrop(player.id)
              : () => handleAction("drop", null, player.id);

            const handleTrade = onClickTrade
              ? () => onClickTrade(player.id)
              : () => handleAction("trade", player.id);

            const dropButton = (
              <StyledSpan
                onClick={handleDrop}
                style={{
                  color: "#f00",
                }}
              >
                Drop
              </StyledSpan>
            );

            const tradeButton = (
              <StyledSpan onClick={handleTrade} style={{ color: "#0078f8" }}>
                Trade
              </StyledSpan>
            );

            const addButton = (
              <StyledSpan onClick={handleAdd} style={{ color: "#00ab00" }}>
                Add
              </StyledSpan>
            );

            let actionButton;
            if (isTaken) {
              if (teamId === userTeam.obj.id) {
                actionButton = dropButton;
              } else {
                actionButton = adminMode ? (
                  <>
                    <span>{tradeButton}</span>
                    <span style={{ marginLeft: "0.25rem" }}>{dropButton}</span>
                  </>
                ) : null;
              }
            } else {
              actionButton = addButton;
            }

            return (
              <tr key={i} style={{ height: "2.4rem" }}>
                <td>{actionButton}</td>
                <td>{rank}</td>
                <td>{posRank}</td>
                <td>{player.pos}</td>
                <td className={"left"}>
                  {getPlayerName(player, { short: 1, injury: 1 })}
                </td>
                <td>{nflTeamAbbr}</td>
                <td>{teamCell}</td>
                <td>{byeWeek}</td>
                <td>{stats?.gamesPlayed || 0}</td>
                <td>{(scores?.total || 0).toFixed(1)}</td>
                <td>{(scores?.atg || 0).toFixed(1)}</td>
                <td>{(scores?.avg || 0).toFixed(1)}</td>
                {getStats(ss)}
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        totalCount={numPlayers}
        currentPage={pageNum}
        pageSize={pageSize}
        onPageChange={(page) => {
          parsed.pg = page;
          history.push(`${location.pathname}?${queryString.stringify(parsed)}`);
        }}
      />
      <p style={{ opacity: 0.5, textAlign: "center" }}>
        {pageSize >= numPlayers
          ? `Showing all ${numPlayers} players.`
          : `Showing ${pageSize} of ${numPlayers} players.`}
      </p>
    </div>
  );
}
